import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { getSegmentCode } from '@/shared';
import { readProductsByCodes, type ProductScope } from '@/shared/bfSeller';
import { useClient, useCorporateFlags, useSelectedClientScope } from '@/hooks';
import {
  isProductResolved,
  markProductMisses,
  pinProducts,
  putProductsInDictionary,
  readProductDictionary,
  resetProductDictionaryForScope,
  subscribeProductDictionary,
  unpinProducts,
} from './productDictionaryStore';

/**
 * Acesso React ao cache de produtos já abertos e precificados.
 *
 * Carrinho, checkout, combos e telas de pedido precisam de produto por código, sempre de um
 * conjunto pequeno e conhecido (os itens da tela). Eles declaram os códigos com `ensureProducts`,
 * que abre do IndexedDB só o que ainda não está em cache — e **fixa** o resultado, para que o
 * descarte por teto não tire do carrinho um produto que está sendo somado.
 */

const inFlight = new Map<number, Promise<void>>();

export const useProductScope = (): ProductScope => {
  const { selectedClient } = useClient();
  const { selectedBrandPair } = useCorporateFlags();
  const selectedClientScope = useSelectedClientScope();

  return useMemo(() => {
    const brandLoja = Number(selectedBrandPair?.codLoja);
    const brandSegmento = Number(selectedBrandPair?.codSegmento);

    return {
      codLoja: Number.isFinite(brandLoja) && brandLoja > 0 ? brandLoja : Number(selectedClientScope?.COD_LOJA) || null,
      segmentos:
        Number.isFinite(brandSegmento) && brandSegmento > 0
          ? [brandSegmento]
          : (selectedClientScope?.COD_SEGMENTO || []).map((s: any) => Number(getSegmentCode(s))).filter(Boolean),
      codCliente: selectedClient?.COD_CLIENTE ?? null,
    };
  }, [
    selectedBrandPair?.codLoja,
    selectedBrandPair?.codSegmento,
    selectedClientScope?.COD_LOJA,
    selectedClientScope?.COD_SEGMENTO,
    selectedClient?.COD_CLIENTE,
  ]);
};

export const productScopeSignature = (scope: ProductScope): string =>
  `${scope.codCliente ?? ''}:${scope.codLoja ?? ''}:${(scope.segmentos || []).join(',')}`;

export const useProductDictionary = () => {
  const scope = useProductScope();
  const signature = productScopeSignature(scope);
  const pinnedRef = useRef<number[]>([]);

  useEffect(() => {
    resetProductDictionaryForScope(signature);
    inFlight.clear();
  }, [signature]);

  // Ao desmontar, o que esta tela fixou volta a ser descartável.
  useEffect(
    () => () => {
      unpinProducts(pinnedRef.current);
      pinnedRef.current = [];
    },
    []
  );

  const products = useSyncExternalStore(subscribeProductDictionary, readProductDictionary, readProductDictionary);

  /** Garante que os códigos pedidos estão em cache, fixados enquanto a tela viver. */
  const ensureProducts = useCallback(
    async (codigos: Array<number | string | undefined | null>) => {
      const wanted = Array.from(
        new Set(
          codigos
            .map(Number)
            .filter((cod) => Number.isFinite(cod) && !isProductResolved(cod) && !inFlight.has(cod))
        )
      );

      // Já em cache: só fixa. Fixar não altera o snapshot, então não realimenta o efeito que
      // chamou este `ensureProducts`.
      const jaEmCache = codigos.map(Number).filter((cod) => Number.isFinite(cod) && !wanted.includes(cod));
      if (jaEmCache.length) {
        pinProducts(jaEmCache);
        pinnedRef.current = Array.from(new Set([...pinnedRef.current, ...jaEmCache]));
      }

      if (!wanted.length) return;

      const request = readProductsByCodes(wanted, scope)
        .then((produtos) => {
          const devolvidos = new Set(produtos.map((produto) => Number(produto.COD_PRODUTO)));
          putProductsInDictionary(produtos, { pin: true });
          markProductMisses(wanted.filter((cod) => !devolvidos.has(cod)));
          pinnedRef.current = Array.from(new Set([...pinnedRef.current, ...wanted]));
        })
        .catch(() => {})
        .finally(() => {
          for (const cod of wanted) inFlight.delete(cod);
        });

      for (const cod of wanted) inFlight.set(cod, request);
      await request;
    },
    [scope]
  );

  return { products, ensureProducts };
};
